import React, { createContext, useContext, useState, useCallback } from 'react';
import { shuffle } from 'lodash';
import { Song } from '../components/types';

interface QueueContextType {
  queue: Song[];
  currentQueueIndex: number;
  isQueueActive: boolean;
  addToQueue: (song: Song) => void;
  removeFromQueue: (index: number) => void;
  clearQueue: () => void;
  playQueue: (startIndex?: number) => void;
  stopQueue: () => void;
  nextInQueue: () => Song | null;
  prevInQueue: () => Song | null;
  currentQueueSong: () => Song | null;
  shuffleQueue: () => void;
}

const QueueContext = createContext<QueueContextType | null>(null);

export const useQueue = () => {
  const ctx = useContext(QueueContext);
  if (!ctx) throw new Error('useQueue must be used within QueueProvider');
  return ctx;
};

export const QueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<Song[]>([]);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const [isQueueActive, setIsQueueActive] = useState(false);

  const addToQueue = useCallback((song: Song) => {
    setQueue(prev => [...prev, song]);
  }, []);

  const removeFromQueue = useCallback((index: number) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
    setCurrentQueueIndex(prev => {
      if (index < prev) return prev - 1;
      if (index === prev && prev >= queue.length - 1) return Math.max(0, prev - 1);
      return prev;
    });
  }, [queue.length]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentQueueIndex(0);
    setIsQueueActive(false);
  }, []);

  const playQueue = useCallback((startIndex = 0) => {
    if (queue.length > 0) {
      setCurrentQueueIndex(startIndex);
      setIsQueueActive(true);
    }
  }, [queue.length]);

  const stopQueue = useCallback(() => {
    setIsQueueActive(false);
  }, []);

  const nextInQueue = useCallback(() => {
    if (currentQueueIndex < queue.length - 1) {
      const next = currentQueueIndex + 1;
      setCurrentQueueIndex(next);
      return queue[next];
    }
    setIsQueueActive(false);
    return null;
  }, [currentQueueIndex, queue]);

  const prevInQueue = useCallback(() => {
    if (currentQueueIndex > 0) {
      const prev = currentQueueIndex - 1;
      setCurrentQueueIndex(prev);
      return queue[prev];
    }
    return queue[0] || null;
  }, [currentQueueIndex, queue]);

  const currentQueueSong = useCallback(() => {
    return queue[currentQueueIndex] || null;
  }, [queue, currentQueueIndex]);

  const shuffleQueue = useCallback(() => {
    const current = queue[currentQueueIndex];
    const rest = queue.filter((_, i) => i !== currentQueueIndex);
    setQueue([current, ...shuffle(rest)]);
    setCurrentQueueIndex(0);
  }, [queue, currentQueueIndex]);

  return (
    <QueueContext.Provider value={{
      queue, currentQueueIndex, isQueueActive,
      addToQueue, removeFromQueue, clearQueue,
      playQueue, stopQueue, nextInQueue, prevInQueue, currentQueueSong, shuffleQueue,
    }}>
      {children}
    </QueueContext.Provider>
  );
};
